/**
 * OneTrust cookie banner — push-down behaviour.
 *
 * OneTrust (loaded via GTM) injects its banner and styles it. This module makes
 * the banner push the whole page down instead of overlaying it: it measures the
 * banner and writes its height to the --emc-ot-offset custom property, which
 * pads <body> (with a CSS transition, so the site slides down on show and back
 * up on dismiss). CSS pins the banner to the top of the viewport.
 *
 * No dependency on the OneTrust JS API: a MutationObserver + ResizeObserver
 * track the banner appearing, resizing, hiding, or being removed, so any close
 * path (Accept / Reject / Close / preference save) collapses the offset. If the
 * banner never appears (no consent required) this is a no-op.
 */
export function initCookieBanner() {
  const root = document.documentElement;
  const BANNER_ID = 'onetrust-banner-sdk';

  const setOffset = (px) => root.style.setProperty('--emc-ot-offset', `${px}px`);

  const isVisible = (el) => {
    if (!el) return false;
    const cs = getComputedStyle(el);
    return cs.display !== 'none' && cs.visibility !== 'hidden' && el.offsetHeight > 0;
  };

  const measure = () => {
    const banner = document.getElementById(BANNER_ID);
    setOffset(isVisible(banner) ? banner.offsetHeight : 0);
  };

  // Coalesce bursts of mutations into a single measurement per frame.
  let scheduled = false;
  const schedule = () => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      measure();
    });
  };

  const ro = 'ResizeObserver' in window ? new ResizeObserver(schedule) : null;

  const mo = new MutationObserver(() => {
    const banner = document.getElementById(BANNER_ID);
    if (banner && ro) ro.observe(banner); // idempotent for an already-observed node
    schedule();
  });
  mo.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['style', 'class', 'hidden'],
  });

  window.addEventListener('resize', schedule, { passive: true });
  schedule();

  // "Cookies Settings" links (footer, sitemap) open the OneTrust preference
  // center instead of navigating. Delegated so it works whenever the links or
  // OneTrust load.
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-emc-cookie-settings]');
    if (!trigger) return;
    e.preventDefault();
    const ot = window.OneTrust || window.Optanon;
    if (ot && typeof ot.ToggleInfoDisplay === 'function') {
      ot.ToggleInfoDisplay();
    }
  });
}
